import React, {useEffect} from 'react';
import {isDesktop} from 'react-device-detect';
import {FlatList, ListRenderItem, Platform, View} from 'react-native';
import {observer} from 'mobx-react';

import {DEFAULT_CAROUSEL_NUM_ITEMS} from '../../constants/GlobalConstants';
import {LinesParams} from '../../stores/linesStore';
import {useStores} from '../../stores/use-stores';
import {OrderLine} from '../../transport/apiServiceResponse';
import {getProductDisplayRules} from '../../utils/rewards';
import {codeifyName} from '../../utils/string';
import {visibleScrollbarProps} from '../../utils/webScrollbar';
import {ProductTileCarousel} from '../ProductTile';
import TitleLine from '../rewards/TitleLine';
import ActivityIndicator from '../themed/ActivityIndicator';
import Text from '../themed/Text';

export type HasCartScreen = {
  /**
   * element is on cart screen so should exclude cart items
   */
  cartScreen?: boolean;
};

type MemberPriceCarouselProps = Omit<ProductCarouselProps, 'data' | 'isLoading'> &
  Required<Pick<LinesParams, 'member_price'>> &
  HasCartScreen;

const MemberPriceCarousel = observer(
  ({title, onSeeAllPress, member_price, cartScreen}: MemberPriceCarouselProps) => {
    const {cartStore, getLinesStore} = useStores();
    const store = getLinesStore({member_price});

    const excludeProductIds = cartScreen
      ? (cartStore.currentOrder?.order_lines || [])?.map(({product_id}) => product_id)
      : [];

    const data = (store?.lines || []).filter(product => {
      const {product_id} = product;
      const isNotAlreadyInCart = !excludeProductIds.includes(product_id);
      const displayRules = getProductDisplayRules(product);
      const showProduct =
        member_price === 'public'
          ? displayRules.presentAsPublicMemberProduct
          : member_price === 'private'
            ? displayRules.presentAsPrivateMemberProduct
            : true;

      return showProduct && isNotAlreadyInCart;
    });

    return (
      <ProductCarousel
        title={title}
        onSeeAllPress={onSeeAllPress}
        data={data}
        isLoading={store?.isLoading}
      />
    );
  },
);

const LastMinuteOptionsCarousel = observer(() => {
  const {cartStore} = useStores();
  const orderLinesIds = cartStore.currentOrder?.order_lines?.map(item => item.product_id) || [];
  const lastMinuteOptionsItems = (cartStore.promotedAtCheckoutProducts || []).filter(
    item => !orderLinesIds.includes(item.product_id),
  );

  useEffect(() => {
    cartStore.getProductsPromotedAtCheckout();
  }, [cartStore]);

  return <ProductCarousel title="LAST MINUTE OPTIONS" data={lastMinuteOptionsItems} />;
});

type ProductCarouselProps = {
  title?: string;

  /**
   * Shows see all button if this is set
   */
  onSeeAllPress?: () => void;
  isLoading?: boolean;
  data?: OrderLine[];
  numItems?: number;
};

function ProductCarousel({
  title,
  onSeeAllPress,
  isLoading,
  data,
  numItems = DEFAULT_CAROUSEL_NUM_ITEMS,
}: ProductCarouselProps) {
  const titleId = codeifyName(title ?? '');

  return (
    <View testID={`product-carousel-${titleId}`}>
      {!!title && <TitleLine title={title} onSeeAllPress={onSeeAllPress} />}
      {data?.length === 0 && !isLoading ? (
        <Empty />
      ) : (
        <FlatList
          contentContainerStyle={{paddingBottom: 1}}
          data={data?.slice(0, numItems)}
          keyExtractor={item => String(item.product_id)}
          renderItem={renderItem}
          ItemSeparatorComponent={Spacer}
          horizontal
          showsHorizontalScrollIndicator={isDesktop}
          {...visibleScrollbarProps(isDesktop)}
          ListEmptyComponent={ActivityIndicator}
          windowSize={3}
          maxToRenderPerBatch={3}
          initialNumToRender={3}
          removeClippedSubviews={Platform.OS === 'android'}
        />
      )}
    </View>
  );
}

const Empty = () => {
  return <Text>No products found.</Text>;
};

const renderItem: ListRenderItem<OrderLine> = ({item}) => {
  return <ProductTileCarousel item={item} key={item.product_id} testContext="carousel" />;
};

function Spacer() {
  return <View style={{height: 8, width: 8}} />;
}

export {LastMinuteOptionsCarousel, MemberPriceCarousel, ProductCarousel};
